import { useId, type ReactNode } from 'react'
import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import DnsOutlinedIcon from '@mui/icons-material/DnsOutlined'
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded'
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined'
import ThermostatOutlinedIcon from '@mui/icons-material/ThermostatOutlined'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  Button,
  Dialog,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useSelector } from 'react-redux'
import { controllerTokens } from '@/features/config/pages/Controllers/uiTokens'
import { usePermission } from '@/hooks/permissions'
import { useTranslation } from '@/hooks/useTranslation'
import { selectTheme } from '@/store/selectors/ThemeSelector'
import { mode_type } from '@/theme/types/constants'
import { StationDeletionMemberships } from './StationDeletionMemberships'
import {
  stationDeletionDangerButtonSx,
  stationDeletionSecondaryButtonSx,
} from './stationDeletionStyles'
import { useStationDeletion } from './useStationDeletion'
import type { StationDeletionSource } from './stationDeletionSnapshot'

interface DeleteStationActionProps {
  interfaceId: number
  station: StationDeletionSource
  disabled?: boolean
  onDeleted: () => void
}

interface NoticeProps {
  icon: ReactNode
  bg: string
  border: string
  children: ReactNode
}

const Notice = ({ icon, bg, border, children }: NoticeProps) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
      p: '14px 16px',
      borderRadius: '12px',
      backgroundColor: bg,
      border: `1px solid ${border}`,
    }}
  >
    <Box sx={{ flexShrink: 0, display: 'flex', mt: '2px' }}>{icon}</Box>
    <Box sx={{ minWidth: 0, flex: 1 }}>{children}</Box>
  </Box>
)

export const StationDeletionAction = ({
  interfaceId,
  station,
  disabled = false,
  onDeleted,
}: DeleteStationActionProps): JSX.Element | null => {
  const { t } = useTranslation()
  const theme = useTheme()
  const themeMode = useSelector(selectTheme)
  const isDark = themeMode === mode_type.dark || theme.palette.mode === 'dark'
  const c = controllerTokens(isDark)

  const dangerSoft = isDark ? 'rgba(255, 107, 107, 0.12)' : 'rgba(192, 57, 43, 0.08)'
  const dangerLine = isDark ? 'rgba(255, 107, 107, 0.32)' : 'rgba(192, 57, 43, 0.22)'

  const { isViewOnly, isLoading, isAccessControlLoading } = usePermission()
  const allowed = !isViewOnly && !isLoading && !isAccessControlLoading
  const deletion = useStationDeletion({ interfaceId, station, allowed, disabled, onDeleted })

  const titleId = useId()
  const descriptionId = useId()
  const runningNoteId = useId()

  if (isViewOnly) return null

  const stationName = deletion.station?.description || String(station.id)
  const hasDecoderInfo =
    deletion.station?.address !== undefined && deletion.station?.index !== undefined

  return (
    <Stack spacing={2} sx={{ mt: 3 }}>
      <Divider />
      <Button
        type="button"
        variant="contained"
        color="error"
        startIcon={<DeleteOutlineIcon />}
        onClick={deletion.openDialog}
        disabled={deletion.unavailable}
        aria-describedby={deletion.running ? runningNoteId : undefined}
        sx={{ ...stationDeletionDangerButtonSx, alignSelf: 'flex-start' }}
      >
        {t('stationDeleteAction')}
      </Button>

      {deletion.running && (
        <Typography id={runningNoteId} role="status" color="text.main" sx={{ lineHeight: 1.5 }}>
          {t('stationDeleteRunning')}
        </Typography>
      )}

      {!deletion.open && deletion.errorMessage && (
        <Notice
          icon={<ErrorRoundedIcon sx={{ fontSize: 20, color: c.danger }} />}
          bg={dangerSoft}
          border={dangerLine}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
            <Typography sx={{ fontSize: '13px', color: c.text, lineHeight: 1.5 }}>
              {deletion.errorMessage}
            </Typography>
            <Button
              type="button"
              variant="outlined"
              size="small"
              onClick={deletion.retry}
              sx={{
                ...stationDeletionSecondaryButtonSx,
                minHeight: '28px',
                height: '28px',
                px: 1.5,
                fontSize: '12px',
                flexShrink: 0,
              }}
            >
              {t('stationDeleteRetry')}
            </Button>
          </Box>
        </Notice>
      )}

      <Dialog
        open={deletion.open}
        onClose={deletion.closeDialog}
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        sx={{
          '& .MuiDialog-paper': {
            width: 540,
            maxWidth: 'calc(100% - 32px)',
            borderRadius: '16px',
            backgroundColor: c.card,
            backgroundImage: 'none',
            border: `1px solid ${c.line}`,
            boxShadow: isDark
              ? '0 24px 48px -12px rgba(0, 0, 0, 0.7), 0 0 0 1px rgba(255, 255, 255, 0.08)'
              : '0 24px 48px -12px rgba(16, 24, 40, 0.18), 0 0 0 1px rgba(16, 24, 40, 0.06)',
            overflow: 'hidden',
          },
        }}
      >
        <Box sx={{ p: { xs: '20px', sm: '28px 28px 24px' }, position: 'relative' }}>
          {/* Close button */}
          <IconButton
            aria-label={t('commonActionsCancel')}
            onClick={deletion.closeDialog}
            disabled={deletion.deleting}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              color: c.muted,
              transition: 'all 0.2s ease',
              '&:hover': {
                color: c.textHi,
                backgroundColor: c.hover,
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>

          {/* Header with Circular Danger Icon Badge */}
          <Stack direction="row" spacing="16px" alignItems="center" sx={{ pr: '40px' }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                flexShrink: 0,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: c.danger,
                backgroundColor: dangerSoft,
                border: `1px solid ${dangerLine}`,
              }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                id={titleId}
                sx={{
                  fontSize: '20px',
                  fontWeight: 700,
                  color: c.textHi,
                  lineHeight: 1.25,
                }}
              >
                {t('stationDeleteAction')}
              </Typography>
              <Typography
                sx={{
                  fontSize: '14.5px',
                  fontWeight: 600,
                  color: c.text,
                  mt: '3px',
                  overflowWrap: 'anywhere',
                }}
              >
                {stationName}
              </Typography>
              {hasDecoderInfo && (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    mt: '5px',
                    px: '8px',
                    py: '3px',
                    borderRadius: '6px',
                    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
                    border: `1px solid ${c.lineSoft}`,
                  }}
                >
                  <DnsOutlinedIcon sx={{ fontSize: 13, color: c.muted }} />
                  <Typography sx={{ fontSize: '12px', color: c.muted, fontWeight: 500 }}>
                    {t('stationDeleteDecoder', {
                      address: deletion.station?.address,
                      index: deletion.station?.index,
                    })}
                  </Typography>
                </Box>
              )}
            </Box>
          </Stack>

          {/* Warning Confirmation Notice */}
          <Box sx={{ mt: '20px' }}>
            <Notice
              icon={<WarningAmberRoundedIcon sx={{ fontSize: 22, color: c.danger }} />}
              bg={dangerSoft}
              border={dangerLine}
            >
              <Typography
                id={descriptionId}
                sx={{
                  fontSize: '13.5px',
                  color: c.text,
                  lineHeight: 1.55,
                }}
              >
                {t('stationDeleteConfirm', { name: stationName })}
              </Typography>
            </Notice>
          </Box>

          {/* Loading Impact Skeleton */}
          {deletion.impact.isFetching && (
            <Box
              sx={{
                mt: '16px',
                p: '16px',
                borderRadius: '12px',
                backgroundColor: c.page,
                border: `1px solid ${c.line}`,
              }}
              role="status"
              aria-label={t('stationDeleteLoading')}
            >
              <Stack spacing={1.5}>
                <Skeleton variant="text" width="30%" height={20} />
                <Skeleton variant="rounded" width="100%" height={34} />
                <Skeleton variant="text" width="40%" height={20} />
                <Skeleton variant="rounded" width="100%" height={34} />
              </Stack>
            </Box>
          )}

          {/* Impact Memberships (Programs & Microclimates) */}
          {!deletion.impact.isFetching && deletion.impact.data && (
            <Box
              sx={{
                mt: '16px',
                p: '16px',
                borderRadius: '12px',
                backgroundColor: c.page,
                border: `1px solid ${c.line}`,
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
              }}
            >
              <StationDeletionMemberships
                title={t('stationDeletePrograms')}
                icon={<ScheduleOutlinedIcon sx={{ fontSize: 17, color: c.muted }} />}
                items={deletion.impact.data.programs}
                isDark={isDark}
                tokens={c}
              />
              <Divider sx={{ borderColor: c.line }} />
              <StationDeletionMemberships
                title={t('stationDeleteMicroclimates')}
                icon={<ThermostatOutlinedIcon sx={{ fontSize: 17, color: c.muted }} />}
                items={deletion.impact.data.microclimates}
                isDark={isDark}
                tokens={c}
              />
            </Box>
          )}

          {/* Error / Running Notice */}
          {(deletion.errorMessage || deletion.running) && (
            <Box sx={{ mt: '16px' }}>
              <Notice
                icon={<ErrorRoundedIcon sx={{ fontSize: 20, color: c.danger }} />}
                bg={dangerSoft}
                border={dangerLine}
              >
                <Typography sx={{ fontSize: '13px', color: c.danger, fontWeight: 500 }}>
                  {deletion.errorMessage || t('stationDeleteRunning')}
                </Typography>
              </Notice>
            </Box>
          )}

          {/* Retry Button */}
          {deletion.impact.isError && (
            <Button
              type="button"
              variant="outlined"
              onClick={deletion.retry}
              sx={{
                mt: '12px',
                alignSelf: 'flex-start',
                height: 36,
                borderRadius: '8px',
                fontSize: '13px',
                color: c.text,
                borderColor: c.line,
                textTransform: 'none',
                '&:hover': { borderColor: c.text, backgroundColor: c.hover },
              }}
            >
              {t('stationDeleteRetry')}
            </Button>
          )}

          {/* Actions: Cancel & Confirm Delete */}
          <Stack
            direction="row"
            justifyContent="flex-end"
            spacing="12px"
            sx={{ mt: '24px' }}
          >
            <Button
              type="button"
              variant="outlined"
              disabled={deletion.deleting}
              onClick={deletion.closeDialog}
              sx={{
                minWidth: '110px',
                height: '40px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 600,
                color: c.text,
                backgroundColor: c.card,
                border: `1px solid ${c.line}`,
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: c.hover,
                  borderColor: c.text,
                },
              }}
            >
              {t('commonActionsCancel')}
            </Button>
            <LoadingButton
              type="button"
              loading={deletion.deleting}
              disabled={deletion.confirmDisabled}
              onClick={deletion.confirm}
              sx={{
                minWidth: '140px',
                height: '40px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 600,
                color: '#FFFFFF',
                backgroundColor: c.danger,
                textTransform: 'none',
                boxShadow: isDark
                  ? '0 2px 8px rgba(255, 107, 107, 0.25)'
                  : '0 2px 8px rgba(192, 57, 43, 0.25)',
                '&:hover': {
                  backgroundColor: isDark ? '#ff5252' : '#a93226',
                  boxShadow: isDark
                    ? '0 4px 12px rgba(255, 107, 107, 0.35)'
                    : '0 4px 12px rgba(192, 57, 43, 0.35)',
                },
                '&.Mui-disabled': {
                  color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(255,255,255,0.8)',
                  backgroundColor: isDark ? '#2b4552' : '#a3b8b7',
                  boxShadow: 'none',
                },
              }}
            >
              {t('stationDeleteAction')}
            </LoadingButton>
          </Stack>
        </Box>
      </Dialog>
    </Stack>
  )
}
